<template>
  <a-modal
    :title="title"
    :visible="open"
    :maskClosable="false"
    :keyboard="false"
    :width="720"
    @cancel="handleCloseKeepRunning"
  >
    <template slot="footer">
      <a-button @click="handleCancelRemaining" :disabled="!hasActiveTasks">取消剩余</a-button>
      <a-button @click="handleRetryFailed" :disabled="counts.failed === 0">重试失败</a-button>
      <a-button @click="handleCloseKeepRunning">关闭（后台继续）</a-button>
      <a-button type="primary" @click="handleDoneClose" :disabled="hasActiveTasks">
        完成并关闭
      </a-button>
    </template>

    <a-alert
      type="info"
      show-icon
      style="margin-bottom: 12px"
    >
      <div slot="message">
        <div>适合大批量长传：并发限流、逐集入库。</div>
        <div style="margin-top:6px"><b>视频命名：</b>1.mp4 或 短剧ID-1.mp4（用集数命名）。</div>
        <div style="margin-top:4px"><b>字幕上传要求：</b></div>
        <ul style="margin:4px 0 0 18px;padding:0;line-height:1.7">
          <li>格式仅支持 .srt / .vtt</li>
          <li>文件名必须带集数，与对应视频集数一致，如 1.srt、12.vtt，或 短剧ID-1.srt</li>
          <li>按语种分区上传：印尼语 / 英语 / 法语 / 阿拉伯语 / 马来语；点错语种区会写到错误字段</li>
          <li>与视频按「集数」对应，不按选择顺序；可先传字幕或先传视频</li>
          <li>同一集同一语种重复上传会覆盖该集该语种字幕</li>
        </ul>
      </div>
    </a-alert>

    <a-row :gutter="16" style="margin-bottom: 12px">
      <a-col :span="4"><a-statistic title="总计" :value="counts.total" /></a-col>
      <a-col :span="4"><a-statistic title="待传" :value="counts.pending" /></a-col>
      <a-col :span="4"><a-statistic title="上传中" :value="counts.uploading + counts.saving" :value-style="{ color: '#cf1322' }" /></a-col>
      <a-col :span="4"><a-statistic title="已入库" :value="counts.success" :value-style="{ color: '#3f8600' }" /></a-col>
      <a-col :span="4"><a-statistic title="失败" :value="counts.failed" :value-style="{ color: '#cf1322' }" /></a-col>
    </a-row>
    <a-progress :percent="overallPercent" :status="counts.failed > 0 && !hasActiveTasks ? 'exception' : 'active'" />

    <a-card title="剧集视频 (.mp4)" size="small" style="margin-top: 12px">
      <a-upload-dragger
        name="file"
        accept=".mp4"
        :multiple="true"
        :show-upload-list="false"
        :customRequest="noopRequest"
        :beforeUpload="beforeVideoUpload"
      >
        <p class="ant-upload-text">点击或拖拽选择多个 mp4（并发限流上传）</p>
      </a-upload-dragger>
    </a-card>

    <a-card title="字幕（.srt / .vtt，文件名=集数，如 1.srt）" size="small" style="margin-top: 12px">
      <a-row :gutter="8">
        <a-col :span="12" style="margin-bottom: 8px">
          <div class="sub-label">印尼语</div>
          <a-upload :multiple="true" accept=".srt,.vtt" :show-upload-list="false" :customRequest="noopRequest" :beforeUpload="(f) => beforeSubtitleUpload(f, 'in')">
            <a-button size="small" icon="upload">选择文件</a-button>
          </a-upload>
        </a-col>
        <a-col :span="12" style="margin-bottom: 8px">
          <div class="sub-label">英语</div>
          <a-upload :multiple="true" accept=".srt,.vtt" :show-upload-list="false" :customRequest="noopRequest" :beforeUpload="(f) => beforeSubtitleUpload(f, 'en')">
            <a-button size="small" icon="upload">选择文件</a-button>
          </a-upload>
        </a-col>
        <a-col :span="12" style="margin-bottom: 8px">
          <div class="sub-label">法语</div>
          <a-upload :multiple="true" accept=".srt,.vtt" :show-upload-list="false" :customRequest="noopRequest" :beforeUpload="(f) => beforeSubtitleUpload(f, 'cn')">
            <a-button size="small" icon="upload">选择文件</a-button>
          </a-upload>
        </a-col>
        <a-col :span="12" style="margin-bottom: 8px">
          <div class="sub-label">阿拉伯语</div>
          <a-upload :multiple="true" accept=".srt,.vtt" :show-upload-list="false" :customRequest="noopRequest" :beforeUpload="(f) => beforeSubtitleUpload(f, 'zh')">
            <a-button size="small" icon="upload">选择文件</a-button>
          </a-upload>
        </a-col>
        <a-col :span="12" style="margin-bottom: 8px">
          <div class="sub-label">马来语</div>
          <a-upload :multiple="true" accept=".srt,.vtt" :show-upload-list="false" :customRequest="noopRequest" :beforeUpload="(f) => beforeSubtitleUpload(f, 'bm')">
            <a-button size="small" icon="upload">选择文件</a-button>
          </a-upload>
        </a-col>
      </a-row>
    </a-card>

    <a-card title="任务列表" size="small" style="margin-top: 12px">
      <div v-if="taskList.length === 0" style="color: #999">暂无任务，请选择文件</div>
      <div v-for="item in taskList" :key="item.id" class="task-row">
        <div class="task-main">
          <a-tag :color="statusColor(item.status)">{{ statusText(item.status) }}</a-tag>
          <span class="task-name">{{ item.kind === 'video' ? '视频' : ('字幕-' + langLabel(item.subtitleLang)) }} · 第{{ item.serialNumber }}集 · {{ item.name }}</span>
        </div>
        <a-progress v-if="item.status === 'uploading' || item.status === 'saving'" :percent="item.percent" :show-info="true" size="small" />
        <div v-if="item.status === 'failed'" class="task-error">
          {{ item.error }}
          <a @click="retryTask(item.id)">重试</a>
        </div>
      </div>
    </a-card>
  </a-modal>
</template>

<script>
import { importData } from '@/api/video/gather'
import UploadFileToVOD from '@/api/vod'
import { uploadToCos } from '@/utils/upload-to-cos'
import {
  createEpisodeUploadQueue,
  parseSerialNumber
} from '@/utils/episode-upload-queue'
import { updateBatchSession, clearBatchSession } from '@/utils/episode-batch-session'

const SUBTITLE_PREFIX = {
  in: 'subtitle/in',
  en: 'subtitle/en',
  cn: 'subtitle/cn',
  zh: 'subtitle/zh',
  bm: 'subtitle/bm'
}

const SUBTITLE_FIELD = {
  in: 'subtitleIn',
  en: 'subtitleEn',
  cn: 'subtitleCn',
  zh: 'subtitle',
  bm: 'subtitleBm'
}

const SUBTITLE_LIST_KEY = {
  in: 'inList',
  en: 'enList',
  cn: 'cnList',
  zh: 'zhList',
  bm: 'bmList'
}

export default {
  name: 'BatchImportExcel',
  data() {
    return {
      title: '批量导入剧集',
      open: false,
      id: '',
      taskList: [],
      counts: {
        total: 0,
        pending: 0,
        uploading: 0,
        saving: 0,
        success: 0,
        failed: 0,
        cancelled: 0
      },
      overallPercent: 0,
      queue: null,
      refreshTimer: null
    }
  },
  computed: {
    hasActiveTasks() {
      return this.counts.pending > 0 || this.counts.uploading > 0 || this.counts.saving > 0
    }
  },
  beforeDestroy() {
    this.clearRefreshTimer()
  },
  methods: {
    noopRequest() {},
    langLabel(lang) {
      return ({ in: '印尼', en: '英', cn: '法', zh: '阿', bm: '马' })[lang] || lang
    },
    statusText(status) {
      return ({
        pending: '待传',
        uploading: '上传中',
        saving: '入库中',
        success: '已入库',
        failed: '失败',
        cancelled: '已取消'
      })[status] || status
    },
    statusColor(status) {
      return ({
        pending: 'default',
        uploading: 'blue',
        saving: 'orange',
        success: 'green',
        failed: 'red',
        cancelled: 'default'
      })[status] || 'default'
    },
    ensureQueue() {
      if (this.queue) {
        return this.queue
      }
      const that = this
      this.queue = createEpisodeUploadQueue({
        concurrency: 2,
        onChange(snap) {
          that.taskList = snap.tasks
          that.counts = snap.counts
          that.overallPercent = snap.percent
          updateBatchSession({
            videoId: that.id,
            active: snap.active,
            percent: snap.percent,
            total: snap.counts.total,
            success: snap.counts.success,
            failed: snap.counts.failed,
            pending: snap.counts.pending,
            uploading: snap.counts.uploading + snap.counts.saving,
            message: snap.active
              ? `批量导入中 ${snap.counts.success}/${snap.counts.total}`
              : (snap.counts.total > 0 ? `批量导入结束 成功${snap.counts.success} 失败${snap.counts.failed}` : '')
          })
          if (!snap.active && snap.counts.total > 0 && snap.counts.success > 0) {
            that.$emit('ok')
          }
        }
      })
      return this.queue
    },
    importExcelHandleOpen(e) {
      this.id = e
      this.open = true
      this.ensureQueue()
      updateBatchSession({
        videoId: this.id,
        active: this.hasActiveTasks,
        message: '批量导入已打开'
      })
    },
    beforeVideoUpload(file) {
      const serial = parseSerialNumber(file.name)
      if (serial == null) {
        this.$message.error(`无法解析集数：${file.name}`)
        return false
      }
      const videoId = this.id
      const queue = this.ensureQueue()
      queue.enqueue({
        kind: 'video',
        name: file.name,
        file,
        serialNumber: serial,
        videoId,
        runner: (task, ctx) => this.uploadVideo(task, ctx),
        saver: (task, result) => this.saveVideoEpisode(task, result)
      })
      return false
    },
    beforeSubtitleUpload(file, lang) {
      const serial = parseSerialNumber(file.name)
      if (serial == null) {
        this.$message.error(`无法解析集数：${file.name}`)
        return false
      }
      const videoId = this.id
      const queue = this.ensureQueue()
      queue.enqueue({
        kind: 'subtitle',
        name: file.name,
        file,
        serialNumber: serial,
        subtitleLang: lang,
        videoId,
        runner: (task, ctx) => this.uploadSubtitle(task, ctx),
        saver: (task, result) => this.saveSubtitleEpisode(task, result)
      })
      return false
    },
    uploadVideo(task, ctx) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-new
        new UploadFileToVOD([task.file], {
          success: (urls) => {
            if (ctx.isCancelled()) {
              reject(new Error('已取消'))
              return
            }
            const url = urls && urls[0]
            if (!url) {
              reject(new Error('上传成功但未返回地址'))
              return
            }
            resolve({ url, fileId: task.fileId || '' })
          },
          vProgress: (val) => {
            ctx.onProgress(typeof val === 'number' ? val * 100 : 0)
          },
          fileId: (fid) => {
            task.fileId = fid
          },
          error: (err) => {
            reject(err instanceof Error ? err : new Error((err && err.message) || '视频上传失败'))
          },
          finally: () => {}
        })
      })
    },
    async uploadSubtitle(task, ctx) {
      if (ctx.isCancelled()) {
        throw new Error('已取消')
      }
      const prefix = SUBTITLE_PREFIX[task.subtitleLang] || 'subtitle'
      const url = await uploadToCos(task.file, prefix, (p) => {
        ctx.onProgress(p)
      })
      if (ctx.isCancelled()) {
        throw new Error('已取消')
      }
      return { url }
    },
    async saveVideoEpisode(task, result) {
      const payload = {
        arrList: [{
          serialNumber: task.serialNumber,
          videoUrl: result.url,
          fileId: result.fileId || task.fileId || '',
          videoId: task.videoId
        }],
        enList: [],
        zhList: [],
        cnList: [],
        bmList: [],
        inList: []
      }
      await this.callImport(payload)
    },
    async saveSubtitleEpisode(task, result) {
      const field = SUBTITLE_FIELD[task.subtitleLang]
      const listKey = SUBTITLE_LIST_KEY[task.subtitleLang]
      const item = {
        serialNumber: task.serialNumber,
        videoId: task.videoId
      }
      item[field] = result.url
      const payload = {
        arrList: [],
        enList: [],
        zhList: [],
        cnList: [],
        bmList: [],
        inList: []
      }
      payload[listKey] = [item]
      await this.callImport(payload)
    },
    async callImport(payload) {
      const response = await importData(JSON.stringify(payload))
      const ok = response && (response.code == 1 || response.code == 20001 || response.success === true)
      if (!ok) {
        throw new Error((response && response.message) || '入库失败')
      }
      const data = response.data
      if (data && data.skipCount > 0 && data.successCount === 0) {
        const msg = (data.skipMessages && data.skipMessages[0]) || '入库被跳过'
        throw new Error(msg)
      }
      return data
    },
    retryTask(taskId) {
      this.ensureQueue().retry(taskId)
    },
    handleRetryFailed() {
      this.ensureQueue().retryAllFailed()
    },
    handleCancelRemaining() {
      this.ensureQueue().cancelPending()
      this.$message.info('已取消未开始的任务')
    },
    handleCloseKeepRunning() {
      this.open = false
      if (this.hasActiveTasks) {
        this.$message.success('已关闭窗口，上传在后台继续')
      }
    },
    handleDoneClose() {
      this.open = false
      if (!this.hasActiveTasks) {
        clearBatchSession()
        this.$emit('ok')
        if (this.queue) {
          this.queue.reset()
          this.queue = null
        }
        this.taskList = []
        this.counts = {
          total: 0,
          pending: 0,
          uploading: 0,
          saving: 0,
          success: 0,
          failed: 0,
          cancelled: 0
        }
        this.overallPercent = 0
      }
    },
    clearRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    }
  }
}
</script>

<style scoped>
.sub-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.task-row {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.task-main {
  display: flex;
  align-items: center;
  gap: 8px;
}
.task-name {
  font-size: 13px;
  word-break: break-all;
}
.task-error {
  color: #cf1322;
  font-size: 12px;
  margin-top: 4px;
}
.task-error a {
  margin-left: 8px;
}
</style>
